import { Injectable, inject, OnDestroy } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { environment } from '../../../enviroment';
import { AuthService } from '../services/auth.service';

export interface PmIssueUpdatedEvent {
  tenantId: string;
  projectId: string;
  issueId: string;
  key: string;
  summary: string;
  status: number;
  statusLabel: string;
  assigneeId?: string;
  eventType: string;
  at: string;
}

export interface PmIssueMovedEvent {
  tenantId: string;
  projectId: string;
  boardId: string;
  issueId: string;
  key: string;
  status: number;
  statusLabel: string;
  targetColumnId?: string;
  sortOrder: number;
  at: string;
}

export interface PmNotificationEvent {
  notificationId: string;
  title: string;
  message: string;
  entityType?: string;
  entityId?: string;
  actionUrl?: string;
  at: string;
}

@Injectable({ providedIn: 'root' })
export class PmRealtimeService implements OnDestroy {
  private readonly auth = inject(AuthService);
  private pmConnection: HubConnection | null = null;
  private notificationConnection: HubConnection | null = null;

  readonly issueUpdated$ = new Subject<PmIssueUpdatedEvent>();
  readonly issueMoved$ = new Subject<PmIssueMovedEvent>();
  readonly notificationCreated$ = new Subject<PmNotificationEvent>();

  private hubBase(): string {
    return (environment.apiOrigin || 'http://hr-api.trustedpos.org').replace(/\/$/, '') + '/hubs';
  }

  async ensurePmConnected(): Promise<HubConnection> {
    if (this.pmConnection?.state === HubConnectionState.Connected) {
      return this.pmConnection;
    }

    if (!this.pmConnection) {
      this.pmConnection = new HubConnectionBuilder()
        .withUrl(`${this.hubBase()}/pm`, {
          accessTokenFactory: () => this.auth.getAccessToken() ?? ''
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();

      this.pmConnection.on('issueUpdated', (payload: PmIssueUpdatedEvent) => this.issueUpdated$.next(payload));
      this.pmConnection.on('issueMoved', (payload: PmIssueMovedEvent) => this.issueMoved$.next(payload));
    }

    if (this.pmConnection.state === HubConnectionState.Disconnected) {
      await this.pmConnection.start();
    }

    return this.pmConnection;
  }

  async ensureNotificationsConnected(): Promise<HubConnection> {
    if (this.notificationConnection?.state === HubConnectionState.Connected) {
      return this.notificationConnection;
    }

    if (!this.notificationConnection) {
      this.notificationConnection = new HubConnectionBuilder()
        .withUrl(`${this.hubBase()}/notifications`, {
          accessTokenFactory: () => this.auth.getAccessToken() ?? ''
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Warning)
        .build();

      this.notificationConnection.on('notificationCreated', (payload: PmNotificationEvent) =>
        this.notificationCreated$.next(payload));
    }

    if (this.notificationConnection.state === HubConnectionState.Disconnected) {
      await this.notificationConnection.start();
    }

    return this.notificationConnection;
  }

  async joinBoard(boardId: string): Promise<void> {
    const conn = await this.ensurePmConnected();
    await conn.invoke('JoinBoard', boardId);
  }

  async leaveBoard(boardId: string): Promise<void> {
    if (this.pmConnection?.state !== HubConnectionState.Connected) return;
    await this.pmConnection.invoke('LeaveBoard', boardId);
  }

  async joinProject(projectId: string): Promise<void> {
    const conn = await this.ensurePmConnected();
    await conn.invoke('JoinProject', projectId);
  }

  ngOnDestroy(): void {
    void this.pmConnection?.stop();
    void this.notificationConnection?.stop();
    this.issueUpdated$.complete();
    this.issueMoved$.complete();
    this.notificationCreated$.complete();
  }
}
